'use client';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '../apiFetch';
import { useEffect, useMemo, useRef } from 'react';
import { buildQueryParams } from '../buildQueryParams';

// Generic interface for paginated responses
interface PaginatedResponse<T> {
  [key: string]: T[] | number;
  total: number;
}

interface PaginationOptions<T = unknown> {
  limit?: number;
  search?: string;
  resource: string; // the name of the resource in the response object
  queryKey: readonly unknown[];
  endpoint: string;
  queryFn?: (context: { pageParam: number }) => Promise<PaginatedResponse<T>>;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  retry?: boolean | number;
  retryDelay?: number | ((retryAttempt: number) => number);
  onSuccess?: (data: PaginatedResponse<T>) => void;
  onError?: (error: unknown) => void;
  onSettled?: (data: PaginatedResponse<T> | undefined, error: unknown) => void;
  // Accept any additional parameters
  params?: Record<string, string | number | boolean | undefined | null>;
}

export const usePagination = <T = unknown>(options: PaginationOptions<T>) => {
  const {
    limit = 10,
    endpoint,
    queryKey,
    queryFn,
    search,
    resource,
    params = {},
    ...restOptions
  } = options;

  // Create a comprehensive queryKey that includes all dependencies
  const comprehensiveQueryKey = [...queryKey, search, params];

  const defaultQueryFn = async ({ pageParam = 1 }: { pageParam: number }) => {
    // Build query parameters dynamically, including all provided params
    const queryParams = buildQueryParams({
      page: pageParam,
      limit,
      ...(search && { search }), // Only include search if it has a value
      ...params, // Include any additional parameters
    });

    const res = await apiFetch(`/api/v1/${endpoint}${queryParams}`, {
      method: 'GET',
    });

    if (typeof res == 'object' && !!res && 'data' in res) {
      return res.data as PaginatedResponse<T>;
    } else {
      return res as PaginatedResponse<T>;
    }
  };

  const query = useInfiniteQuery({
    queryKey: comprehensiveQueryKey,
    queryFn: queryFn || defaultQueryFn,
    getNextPageParam: (
      lastPage: PaginatedResponse<T>,
      allPages: PaginatedResponse<T>[]
    ) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(lastPage.total / limit);

      // If we've loaded all pages, return undefined
      if (currentPage >= totalPages) {
        return undefined;
      }

      // Return the next page number
      return currentPage + 1;
    },
    initialPageParam: 1,
    ...restOptions,
  });

  // Calculate if all data is loaded
  const totalCount = query.data?.pages[0]?.total ?? 0;
  const loadedCount =
    query.data?.pages.reduce(
      (acc, page) => acc + (page[resource] as T[]).length,
      0
    ) ?? 0;
  const isAllDataLoaded = query.isSuccess && loadedCount >= totalCount;
  const allItems = useMemo(() => {
    return query.data?.pages.flatMap((page) => page[resource] as T[]) ?? [];
  }, [query.data, resource]);

  return {
    ...query,
    // Flatten all pages into a single array of items
    allItems,
    // Get total count from the first page
    totalCount,
    // Check if all data is loaded
    isAllDataLoaded,
    // Additional useful info
    loadedCount,
    remainingCount: Math.max(0, totalCount - loadedCount),
  };
};

// The hook that used to detect the end of the scroll

type UseInfiniteScrollOptions = {
  root?: HTMLElement | null; // scroll container, default = viewport
  rootMargin?: string; // e.g. "0px 0px 200px 0px"
  threshold?: number; // intersection threshold
  onLoadMore: () => void; // callback when reached end
};

export function useInfiniteScroll({
  root = null,
  rootMargin = '0px',
  threshold = 0.1,
  onLoadMore,
}: UseInfiniteScrollOptions) {
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [root, rootMargin, threshold, onLoadMore]);

  return loaderRef;
}
